import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "dental-seo-colorado",
  metaTitle: "Dental SEO in Colorado | Local SEO for Dentists & Practices",
  metaDescription:
    "Dental SEO across Colorado — Map Pack rankings, new-patient growth, and content built for high-value procedures. Owner-led. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Dental marketing", to: "/dental-marketing-colorado" },
  ],
  currentLabel: "Dental SEO Colorado",
  eyebrow: "Dental SEO — Colorado",
  h1: (
    <>
      Dental <span className="text-brand-gold">SEO</span> in Colorado
    </>
  ),
  heroDescription:
    "Win the searches that drive new-patient acquisition — implants, Invisalign, cosmetic, emergency, and family dentistry across every Colorado market.",
  intro:
    "Most dental SEO is generic — one 'dentist near me' page and a glossy hero photo. We build dedicated ranking pages for the high-value procedures that actually grow practices: dental implants, Invisalign, cosmetic dentistry, emergency dentistry, sedation, and pediatric. Each page is FAQ-rich, schema-marked-up, and tied into a properly tuned Google Business Profile that captures the searches turning into booked exams.",
  definition: {
    term: "Dental SEO in Colorado",
    body: "Dental SEO is local search optimization that ranks dental practices for high-intent searches like 'dental implants Denver', 'Invisalign Colorado Springs', or 'emergency dentist near me' — through Map Pack work, procedure-page content, and review velocity.",
  },
  whatWeDo: [
    { title: "One ranking page per high-value procedure", body: "Implants, Invisalign, veneers, crowns, sedation, emergency, pediatric — each with its own FAQ-rich page." },
    { title: "Map Pack work in every Colorado market", body: "Colorado Springs, Denver, Fort Collins, Boulder, Castle Rock — neighborhood-deep targeting for new-patient capture." },
    { title: "Insurance-acceptance pages", body: "Dedicated content for Delta Dental, Cigna, MetLife, and other major insurance plans Colorado patients search for." },
    { title: "Review velocity tied to appointments", body: "HIPAA-compliant review-request workflow tied to your PMS (Dentrix, Eaglesoft, Open Dental) so reviews come in steadily." },
    { title: "Schema for dental trust signals", body: "Dentist, MedicalBusiness, Service, FAQPage, and Review schema — plus credentials, insurance accepted, and procedure detail surfaced on-page." },
    { title: "Mobile speed and conversion", body: "Sub-2-second mobile load, sticky click-to-call, and online booking flow that converts search into scheduled exams." },
  ],
  whyThisMarket: [
    {
      heading: "High-value procedures justify deep content investment",
      body: "A single dental implant case in Colorado runs $4K–$8K. Invisalign averages $5K. Ranking for those terms — even at low volume — pays for the entire SEO investment many times over.",
    },
    {
      heading: "Most dental sites rank only for the practice name",
      body: "We routinely audit Colorado dental sites that rank #1 for the practice name and nowhere for procedures. The practice name brings repeat patients; procedure ranking brings new ones. You need both.",
    },
    {
      heading: "Insurance-acceptance content is under-built",
      body: "Patients search 'dentist who accepts Cigna near me' constantly. Most practices bury insurance info in a footer. Building dedicated insurance-acceptance pages captures that intent directly.",
    },
  ],
  relatedLinks: [
    { to: "/dental-marketing-colorado", label: "Dental marketing in Colorado — full hub" },
    { to: "/colorado-springs-seo", label: "How our SEO process works" },
    { to: "/colorado-springs-google-maps", label: "Local SEO & Google Maps overview" },
    { to: "/dental-google-maps-colorado", label: "Dental Google Maps optimization" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/denver-digital-marketing", label: "Denver digital marketing" },
    { to: "/case-studies", label: "Colorado dental case studies" },
    { to: "/seo", label: "Our SEO service (all industries)" },
    { to: "/local-seo", label: "Local SEO overview" },
  ],
  faqs: [
    { q: "How long does dental SEO take to work in Colorado?", a: "Most Colorado dental practices see Map Pack movement in 60–120 days and stronger new-patient flow by month 4–6. Dental is competitive in Denver and Colorado Springs but more open in suburbs and smaller cities." },
    { q: "Should I focus on general dentistry or premium procedures?", a: "Both, but on different pages. General dentistry pages drive volume; premium-procedure pages drive revenue. We typically build a strong general homepage path with dedicated ranking pages for each high-value procedure." },
    { q: "Are dental review workflows HIPAA-compliant?", a: "Yes — when set up correctly. We use HIPAA-compliant review platforms (Birdeye, Podium, NiceJob) and configure them so no PHI flows into the review request itself. Just compliant, systematic outreach to satisfied patients." },
    { q: "Do you only serve Colorado Springs dental practices?", a: "No — we serve dentists across all of Colorado: Denver, Fort Collins, Boulder, Pueblo, Castle Rock, Parker, and the Front Range corridor." },
  ],
  serviceName: "Dental SEO in Colorado",
  serviceDescription:
    "Local SEO and Map Pack optimization for dental practices across Colorado, with focus on high-value procedures and new-patient acquisition.",
  areaServed: "Colorado",
  // SEO: consolidate ranking signal onto the /dental-marketing-colorado flagship hub.
  canonicalOverride: "https://coloradowebimpressions.com/dental-marketing-colorado",
};

export const Route = createFileRoute("/dental-seo-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
